import { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { useDialog } from "@/context/DialogProvider";
import {
  Users as UsersIcon, Plus, Trash2, RefreshCw, Copy, Check, Pencil, X as XIcon,
} from "lucide-react";
import StepLayout from "./StepLayout";

const ROLE_OPTIONS = [
  { value: "manager", label: "Manager", help: "Multi-site oversight. Can run reports, approve CAPAs, manage team." },
  { value: "supervisor", label: "Supervisor", help: "Site-level lead. Can verify daily checks and resolve incidents." },
  { value: "staff", label: "Staff", help: "Day-to-day operators. Log readings, run daily checks." },
];

function genTempPassword() {
  const charset = "abcdefghjkmnpqrstuvwxyz23456789";
  let s = ""; for (let i = 0; i < 8; i++) s += charset[Math.floor(Math.random() * charset.length)];
  return s;
}

/**
 * Step 3 — invite the team.
 *
 * Owner adds team members (one at a time) with a per-member temporary
 * password. We don't enforce a minimum but warn if zero — FSMS team
 * roles in step 6 will need at least three users to be assignable.
 */
export default function Step3Team({ stepIndex, totalSteps, onAdvance, onBack, onFinishLater }) {
  const { notify } = useDialog();
  const [team, setTeam] = useState([]);
  const [sites, setSites] = useState([]);
  const [draft, setDraft] = useState(makeBlankDraft);
  const [createdPwds, setCreatedPwds] = useState({}); // userId -> { email, password }
  const [copiedId, setCopiedId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  function makeBlankDraft() {
    return {
      name: "", email: "", role: "staff", site_ids: [],
      password: genTempPassword(),
      company_role: "",
    };
  }

  const load = async () => {
    try {
      const [{ data: u }, { data: s }] = await Promise.all([
        api.get("/users"),
        api.get("/sites"),
      ]);
      setSites(s.sites || []);
      // Exclude the owner themselves + super_admins from the visible roster.
      setTeam((u.users || []).filter((m) => m.role !== "owner" && m.role !== "super_admin" && !m.is_archived));
    } catch (e) { console.debug("step3 load failed", e); }
  };
  useEffect(() => { load(); }, []);

  const addMember = async () => {
    setErr("");
    if (!draft.name.trim()) return setErr("Member name is required.");
    if (!draft.email.trim()) return setErr("Email is required.");
    if (!draft.password || draft.password.length < 6) return setErr("Password must be at least 6 characters.");
    setBusy(true);
    try {
      const { data } = await api.post("/users", {
        email: draft.email.trim().toLowerCase(),
        password: draft.password,
        name: draft.name.trim(),
        role: draft.role,
        site_ids: draft.site_ids,
        company_role: draft.company_role.trim(),
      });
      const newId = data?.user?.id;
      if (newId) {
        setCreatedPwds((p) => ({
          ...p, [newId]: { email: data.user.email, password: draft.password, name: data.user.name },
        }));
      }
      setDraft(makeBlankDraft());
      await load();
    } catch (ex) {
      await notify({
        title: "Couldn't add member",
        message: formatErr(ex.response?.data?.detail) || "Please check the details and try again.",
        variant: "err",
      });
    }
    finally { setBusy(false); }
  };

  const removeMember = async (uid) => {
    setBusy(true); setErr("");
    try {
      await api.delete(`/users/${uid}`);
      await load();
    } catch (ex) {
      await notify({
        title: "Couldn't remove member",
        message: formatErr(ex.response?.data?.detail) || "Please try again.",
        variant: "err",
      });
    }
    finally { setBusy(false); }
  };

  // Inline edit modal — name/role/sites/job-title. Email + password are
  // intentionally excluded (those go through the dedicated auth flows).
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(null);
  const openEdit = (m) => {
    setEditingId(m.id);
    setEditDraft({
      name: m.name || "",
      role: m.role || "staff",
      site_ids: Array.isArray(m.site_ids) ? [...m.site_ids] : [],
      company_role: m.company_role || "",
      employee_id: m.employee_id || "",
    });
  };
  const closeEdit = () => { setEditingId(null); setEditDraft(null); };
  const toggleEditSite = (siteId) => {
    setEditDraft((d) => {
      const has = d.site_ids.includes(siteId);
      return { ...d, site_ids: has ? d.site_ids.filter((x) => x !== siteId) : [...d.site_ids, siteId] };
    });
  };
  const saveEdit = async () => {
    if (!editDraft) return;
    if (!editDraft.name.trim()) {
      return notify({ title: "Name required", message: "Member name can't be empty.", variant: "err" });
    }
    setBusy(true);
    try {
      await api.put(`/users/${editingId}`, {
        name: editDraft.name.trim(),
        role: editDraft.role,
        site_ids: editDraft.site_ids,
        company_role: editDraft.company_role.trim(),
        employee_id: editDraft.employee_id.trim(),
      });
      closeEdit();
      await load();
    } catch (ex) {
      await notify({
        title: "Couldn't save changes",
        message: formatErr(ex.response?.data?.detail) || "Please try again.",
        variant: "err",
      });
    } finally { setBusy(false); }
  };

  const toggleSite = (siteId) => {
    setDraft((d) => {
      const has = d.site_ids.includes(siteId);
      return { ...d, site_ids: has ? d.site_ids.filter((x) => x !== siteId) : [...d.site_ids, siteId] };
    });
  };

  const copyCreds = async (uid) => {
    const c = createdPwds[uid];
    if (!c) return;
    const txt = `Vanguard Beacon login\nEmail: ${c.email}\nTemp password: ${c.password}\n\nPlease change it after your first sign-in.`;
    try { await navigator.clipboard.writeText(txt); setCopiedId(uid); setTimeout(() => setCopiedId(null), 2000); }
    catch (e) { console.debug("clipboard write failed", e); }
  };

  return (
    <StepLayout
      stepIndex={stepIndex}
      totalSteps={totalSteps}
      title="Bring your team in"
      subtitle="Invite the people who'll log data, run checks, and respond to incidents."
      description={
        <div className="space-y-3">
          <p>Add each team member, assign them sites, and we'll create their login. You'll see a temporary password — share it with them privately and remind them to change it after first sign-in.</p>
          <p className="text-xs opacity-70">Tip: you'll need at least one manager + one team member to fully appoint the FSMS committee in a later step.</p>
        </div>
      }
      onBack={onBack}
      onNext={() => onAdvance({ team: true })}
      onFinishLater={onFinishLater}
      busy={busy}
      errorMsg={err}
    >
      <div className="space-y-5">
        {/* Add member */}
        <div className="vb-card p-6 space-y-3" data-testid="wiz-add-member-card">
          <div className="flex items-center gap-2">
            <UsersIcon size={18} style={{ color: "var(--vb-text-2)" }} />
            <h2 className="vb-heading text-xl m-0" style={{ color: "var(--vb-text-2)" }}>Invite a team member</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Full name *</Label>
              <input className="vb-input" value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                placeholder="e.g. Mary Tan"
                data-testid="wiz-member-name" />
            </div>
            <div>
              <Label>Work email *</Label>
              <input type="email" className="vb-input" value={draft.email}
                onChange={(e) => setDraft({ ...draft, email: e.target.value })}
                placeholder="mary.tan@yourcompany.com"
                data-testid="wiz-member-email" />
            </div>
            <div>
              <Label>Role *</Label>
              <select className="vb-input" value={draft.role}
                onChange={(e) => setDraft({ ...draft, role: e.target.value })}
                data-testid="wiz-member-role">
                {ROLE_OPTIONS.map((r) => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
              <p className="text-[11px] opacity-60 mt-1 m-0">
                {ROLE_OPTIONS.find((r) => r.value === draft.role)?.help}
              </p>
            </div>
            <div>
              <Label>Job title / dept</Label>
              <input className="vb-input" value={draft.company_role}
                onChange={(e) => setDraft({ ...draft, company_role: e.target.value })}
                placeholder="e.g. Head Chef"
                data-testid="wiz-member-job" />
            </div>
            <div className="sm:col-span-2">
              <Label>Assigned sites</Label>
              {sites.length === 0 ? (
                <div className="text-xs opacity-60">No sites yet — go back to Step 2b to add one.</div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5" data-testid="wiz-member-sites">
                  {sites.map((s) => {
                    const on = draft.site_ids.includes(s.id);
                    return (
                      <label key={s.id}
                        className="flex items-center gap-2 cursor-pointer rounded p-2 hover:bg-[var(--vb-bg-2)]"
                        style={{ border: "1px solid var(--vb-border)" }}
                        data-testid={`wiz-member-site-${s.id}`}>
                        <input type="checkbox" checked={on}
                          onChange={() => toggleSite(s.id)}
                          data-testid={`wiz-member-site-${s.id}-cb`} />
                        <span className="text-sm">{s.name}</span>
                      </label>
                    );
                  })}
                </div>
              )}
              <p className="text-[11px] opacity-60 mt-1.5 m-0">
                Leave all unchecked for a chain-wide manager (sees all sites). Required for staff/supervisor roles.
              </p>
            </div>
            <div className="sm:col-span-2">
              <Label>Temporary password</Label>
              <div className="flex gap-2">
                <input className="vb-input flex-1 font-mono" value={draft.password}
                  onChange={(e) => setDraft({ ...draft, password: e.target.value })}
                  data-testid="wiz-member-pwd" />
                <button type="button" className="vb-btn ghost sm"
                  onClick={() => setDraft({ ...draft, password: genTempPassword() })}
                  data-testid="wiz-member-regen">
                  <RefreshCw size={12} />
                </button>
              </div>
              <p className="text-[11px] opacity-60 mt-1 m-0">
                You'll see this once — copy and share it. The user should change it after first sign-in.
              </p>
            </div>
          </div>
          <div className="flex justify-end">
            <button type="button" className="vb-btn" onClick={addMember} disabled={busy}
              data-testid="wiz-member-add-btn">
              <Plus size={14} /> Add member
            </button>
          </div>
        </div>

        {/* Team list */}
        {team.length === 0 ? (
          <div className="vb-card p-6 text-center text-sm opacity-70" data-testid="wiz-team-empty">
            No team members yet — they're optional, but recommended.
          </div>
        ) : (
          <div className="vb-card p-0 overflow-hidden" data-testid="wiz-team-list">
            <table className="vb-table">
              <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Sites</th><th></th></tr></thead>
              <tbody>
                {team.map((m) => {
                  const cred = createdPwds[m.id];
                  const sitesNames = (m.site_ids || []).map((sid) => sites.find((s) => s.id === sid)?.name || "?").join(", ");
                  return (
                    <tr key={m.id} data-testid={`wiz-team-row-${m.id}`}>
                      <td><strong>{m.name}</strong>{m.company_role && <div className="text-xs opacity-60">{m.company_role}</div>}</td>
                      <td className="text-xs opacity-80">{m.email}</td>
                      <td><span className="vb-pill">{m.role}</span></td>
                      <td className="text-xs opacity-80">{sitesNames || "—"}</td>
                      <td className="text-right">
                        <div className="flex gap-1 justify-end">
                          {cred && (
                            <button type="button" className="vb-btn ghost sm"
                              onClick={() => copyCreds(m.id)}
                              title="Copy login credentials"
                              data-testid={`wiz-team-copy-${m.id}`}>
                              {copiedId === m.id ? <Check size={11} /> : <Copy size={11} />}
                              {copiedId === m.id ? " Copied" : " Creds"}
                            </button>
                          )}
                          <button type="button" className="vb-btn ghost sm"
                            onClick={() => openEdit(m)}
                            data-testid={`wiz-team-edit-${m.id}`}>
                            <Pencil size={11} />
                          </button>
                          <button type="button" className="vb-btn ghost sm"
                            onClick={() => removeMember(m.id)} style={{ color: "var(--vb-error)" }}
                            data-testid={`wiz-team-del-${m.id}`}>
                            <Trash2 size={11} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {editingId && editDraft && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(60, 40, 24, 0.6)" }}
          data-testid="wiz-team-edit-modal">
          <div className="vb-card max-w-md w-full p-5 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <h2 className="vb-heading text-lg m-0" style={{ color: "var(--vb-text-2)" }}>Edit team member</h2>
              <button onClick={closeEdit} className="opacity-60 hover:opacity-100"
                data-testid="wiz-team-edit-close"><XIcon size={18} /></button>
            </div>
            <div className="grid gap-3">
              <div>
                <Label>Full name *</Label>
                <input className="vb-input" value={editDraft.name}
                  onChange={(e) => setEditDraft({ ...editDraft, name: e.target.value })}
                  data-testid="wiz-team-edit-name" />
              </div>
              <div>
                <Label>Role *</Label>
                <select className="vb-input" value={editDraft.role}
                  onChange={(e) => setEditDraft({ ...editDraft, role: e.target.value })}
                  data-testid="wiz-team-edit-role">
                  {ROLE_OPTIONS.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Job title / dept</Label>
                <input className="vb-input" value={editDraft.company_role}
                  onChange={(e) => setEditDraft({ ...editDraft, company_role: e.target.value })}
                  data-testid="wiz-team-edit-job" />
              </div>
              <div>
                <Label>Assigned sites</Label>
                {sites.length === 0 ? (
                  <div className="text-xs opacity-60">No sites yet.</div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                    {sites.map((s) => {
                      const on = editDraft.site_ids.includes(s.id);
                      return (
                        <label key={s.id}
                          className="flex items-center gap-2 cursor-pointer rounded p-2"
                          style={{ border: on ? "2px solid var(--vb-accent)" : "1px solid var(--vb-border)" }}
                          data-testid={`wiz-team-edit-site-${s.id}`}>
                          <input type="checkbox" checked={on}
                            onChange={() => toggleEditSite(s.id)}
                            data-testid={`wiz-team-edit-site-${s.id}-cb`} />
                          <span className="text-sm">{s.name}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
                <p className="text-[11px] opacity-60 mt-1 m-0">
                  Leave all unchecked for a chain-wide manager (sees all sites).
                </p>
              </div>
              <div className="text-[11px] opacity-60 pt-1">
                Email and password aren&apos;t editable here — the member can update those from their profile after first sign-in.
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button type="button" className="vb-btn ghost" onClick={closeEdit} disabled={busy}
                data-testid="wiz-team-edit-cancel">Cancel</button>
              <button type="button" className="vb-btn" onClick={saveEdit} disabled={busy}
                data-testid="wiz-team-edit-save">Save</button>
            </div>
          </div>
        </div>
      )}
    </StepLayout>
  );
}

function Label({ children }) {
  return (
    <label className="block text-[11px] font-semibold mb-1 uppercase tracking-wider" style={{ color: "var(--vb-text-2)" }}>
      {children}
    </label>
  );
}
